import axios from "axios";

import type {
	ApplicationNameWithFirstBlockUrl,
	Brain,
	BrainWithCounts,
	ContentUploadStatus,
	UserBrain,
} from "@virtual-brain-lib/types";

import env from "../environments";
import { queryOptions } from "../query-client";

export const DRAG_AND_DROP_BRAIN_NAME = "VirtualBrain - Applications";

export async function createBrain(name: string) {
	const response = await axios.post<Brain>(`${env.apiUrl}/brains/create`, {
		name,
	});

	return response.data;
}

export const getBrainStatus = queryOptions({
	async queryFn({ queryKey }) {
		const [, { brainId }] = queryKey;
		const response = await axios.get<{
			updatedAt: Date;
		}>(`${env.apiUrl}/brains/${brainId}/status`);

		return response.data;
	},
	queryKey: "brainStatus",
});

export async function getBrainFn(id: string) {
	const response = await axios.get<BrainWithCounts>(`${env.apiUrl}/brains/${id}`);

	return response.data;
}

export const getBrain = queryOptions({
	async queryFn({ queryKey }) {
		const [, { brainId }] = queryKey;

		return getBrainFn(brainId);
	},
	queryKey: "brain",
});

export async function getBrainsFn(accessToken?: string | undefined, search?: string) {
	let headers;

	if (accessToken) {
		headers = {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			Authorization: "Bearer " + accessToken,
		};
	}

	const response = await axios.get<Brain[]>(env.apiUrl + "/brains", {
		headers,
		params: {
			search,
		},
	});

	return response.data;
}

export const getBrains = queryOptions({
	async queryFn({ queryKey }) {
		const [, { accessToken, search }] = queryKey;

		return getBrainsFn(accessToken, search);
	},
	queryKey: "brains",
});

export interface GetBrainContentsOptions {
	offset?: number | undefined;
	limit?: number | undefined;
	status?: ContentUploadStatus | undefined;
}

export const updateBrain = async ({
	id,
	updatedBrain,
}: {
	id: string;
	updatedBrain: Partial<Brain>;
}) => {
	const response = await axios.put<Brain>(`${env.apiUrl}/brains/${id}`, updatedBrain);

	return response.data;
};

export const getBrainRelatedApps = queryOptions({
	async queryFn({ queryKey }) {
		const [, { brainId }] = queryKey;

		const response = await axios.get<ApplicationNameWithFirstBlockUrl[]>(
			`${env.apiUrl}/brains/${brainId}/related-apps`,
		);

		return response.data;
	},
	queryKey: "brainRelatedApps",
});

export const deleteBrain = async (id: string) => axios.delete(`${env.apiUrl}/brains/${id}`);

export const getBrainUsersBrains = queryOptions({
	async queryFn({ queryKey }) {
		const [, { brainId }] = queryKey;
		const response = await axios.get<UserBrain[]>(`${env.apiUrl}/brains/sharedWith/${brainId}`);

		return response.data;
	},
	queryKey: "brainUsersBrains",
});

export const transferBrainOwnership = async ({
	brainId,
	newOwnerId,
}: {
	brainId: string;
	newOwnerId: string;
}) => {
	const response = await axios.post<Brain>(`${env.apiUrl}/brains/${brainId}/transfer-ownership`, {
		newOwnerId,
	});

	return response.data;
};
